import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Gitlab } from 'logos';
import { QUERY_KEYS } from 'network/queries/consts';
import { DialogTitleWithIcon, MultiStepDialog } from 'shared/MultiStepDialog';
import { spawn } from 'utils/translations';
import { getIntegrationsCloseTarget } from 'components/GitlabInstallationDialog/utils';
import { getSteps } from './configuration';
import { GitlabInstallationV2Context } from './gitlabInstallationV2Context';
import {
  type GitlabV2ConnectResult,
  type GitlabV2InstallSelection,
  type GitlabV2ScopePaths,
} from './types';

/**
 * v2 GitLab installation wizard — the flag-gated, BE-free shell. Built on the
 * shared MultiStepDialog; renders static step scaffolds and holds cross-step
 * state (the connect URL). One flow for every segment (PAT-only) mounted on the
 * single `/integrations/gitlab` parent — the host (cloud vs self-managed) is
 * derived from the URL the user pastes, not the route. Step behaviors are wired
 * in later.
 */
export const GitlabInstallationDialogV2 = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [gitlabUrl, setGitlabUrl] = useState('');
  // Elevation PAT: in memory only, re-sent per call, cleared on unmount.
  const [accessToken, setAccessToken] = useState<string | undefined>();
  const [connectResult, setConnectResult] = useState<
    GitlabV2ConnectResult | undefined
  >();
  const [isMultiGroupCloud, setIsMultiGroupCloud] = useState(false);
  const [installationId, setInstallationId] = useState<string | undefined>();
  const [installSelection, setInstallSelection] = useState<
    GitlabV2InstallSelection | undefined
  >();
  const [scopePaths, setScopePaths] = useState<GitlabV2ScopePaths>({
    groupPathsById: new Map(),
    projectPathsById: new Map(),
    topLevelGroupIdByGroupId: new Map(),
    topLevelGroupIdByProjectId: new Map(),
  });

  useEffect(() => () => setAccessToken(undefined), []);

  const spawnGeneral = spawn('dialogs.gitlab-installation-v2.general');
  const steps = getSteps();

  // Both onComplete (install done) and onCancel (user closed) route here, so the
  // installations table, repositories, and the GitLab card's count all refetch on
  // either exit (a run can partially install scopes before the user cancels).
  const close = () => {
    navigate(getIntegrationsCloseTarget());
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GIT_INSTALLATIONS] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GIT_REPOSITORIES] });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.INSTALLATIONS_COUNTS],
    });
  };

  return (
    <GitlabInstallationV2Context.Provider
      value={{
        gitlabUrl,
        setGitlabUrl,
        accessToken,
        setAccessToken,
        connectResult,
        setConnectResult,
        isMultiGroupCloud,
        setIsMultiGroupCloud,
        installationId,
        setInstallationId,
        installSelection,
        setInstallSelection,
        scopePaths,
        setScopePaths,
      }}
    >
      <MultiStepDialog
        title={
          <DialogTitleWithIcon text={spawnGeneral('title')}>
            <Gitlab size={24} />
          </DialogTitleWithIcon>
        }
        steps={steps}
        onCancel={close}
        onComplete={close}
        className="sm:max-w-250!"
        disableOutsideClick
        disableEscapeKey
      />
    </GitlabInstallationV2Context.Provider>
  );
};
